import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, Renderer2, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Popr2Ref } from '@ciam/library';
import { TranslateService } from '@ngx-translate/core';
import { EmailVerificationService } from 'src/app/shared/services/email-verification/email-verification.service';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

export enum Keyboard {
  LEFT_ARROW = 'ArrowLeft',
  LEFT = 'Left',
  RIGHT_ARROW = 'ArrowRight',
  RIGHT = 'Right',
  UP_ARROW = 'ArrowUp',
  UP = 'Up',
  DOWN_ARROW = 'ArrowDown',
  DOWN = 'Down',
  BACKSPACE = 'Backspace',
  TAB = 'Tab',
  SHIFT = 'Shift',
  DELETE = 'Delete',
  DEL = 'Del'
}

@Component({
  selector: 'wpro-verify-pin',
  templateUrl: './verify-pin.component.html',
  styleUrls: ['./verify-pin.component.scss'],
  standalone: false
})
export class VerifyPinComponent implements OnInit, AfterViewInit, OnDestroy {
  
  originalEmailId = '';
  newEmailId = '';
  source: 'login' | 'contact' | null = null;
  verifiedResult = false;
  changeEmailRequested = false;
  modalRef: Popr2Ref<any> | null = null;
  
  // Translated subtitle text
  subTitleText = '';

  // OTP Form properties (matching enter-pin)
  verificationCodeForm: UntypedFormGroup;
  digitsStatus = {
    digit1: false,
    digit2: false,
    digit3: false,
    digit4: false,
    digit5: false,
    digit6: false
  };
  digits = Object.keys(this.digitsStatus);
  digitValidStatus = false;
  toggleElement = false;
  questionMarkVisibility = false;
  formSubmit = false;
  expiredAndIncorrectError = false;
  systemUnavailableError = false;
  value = '';
  useDiffMethodLink = false;
  cursorClicked = false;
  isIEBrowser = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);

  @ViewChild('submitBtn') submitBtn: ElementRef;

  // Emitted whenever the popup closes itself (cancel/backdrop/verified/change-email), so the parent can react.
  @Output() closed = new EventEmitter<void>();

  changeChannel = new Subject<any>();

  private removeBackdropListener: (() => void) | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
    private emailVerificationService: EmailVerificationService,
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    // Initialize the 6-digit verification form
    this.verificationCodeForm = this.fb.group({
      digit1: ['', Validators.required],
      digit2: ['', Validators.required],
      digit3: ['', Validators.required],
      digit4: ['', Validators.required],
      digit5: ['', Validators.required],
      digit6: ['', Validators.required]
    });

    // Load translated subtitle with email
    this.loadSubtitle();
    this.sendPin();
  }

  /**
   * Loads the translated subtitle text with the email parameter
   */
  private loadSubtitle(): void {
    this.translateService.get('profileEdit.verifyEmail.subTitle', { 
      email: this.newEmailId 
    }).subscribe((data: string) => {
      this.subTitleText = data;
    });
  }

  ngAfterViewInit(): void {
    const inputElements = this.el.nativeElement.querySelectorAll('input');
    inputElements[0]?.focus();
    this.registerBackdropDismiss();
  }

  ngOnDestroy(): void {
    this.removeBackdropListener?.();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Native <dialog> (showModal) does not close on backdrop click; a target === dialog click is the backdrop.
  private registerBackdropDismiss(): void {
    const dialog: HTMLElement | null = this.el.nativeElement.closest('dialog');
    if (!dialog) {
      return;
    }
    this.removeBackdropListener = this.renderer.listen(dialog, 'click', (event: MouseEvent) => {
      if (event.target === dialog) {
        this.onCancel();
      }
    });
  }

  sendPin(): void {
    this.emailVerificationService.initFRConfig()
      .pipe(
        take(1),
        switchMap(() => this.emailVerificationService.submitNewEmail(this.newEmailId)),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => this.value = this.emailVerificationService.getVerificationAddress(),
        error: () => this.systemUnavailableError = true
      });
  }

  resendPin(): void {
    this.expiredAndIncorrectError = false;
    this.systemUnavailableError = false;
    this.formSubmit = false;
    this.toggleElement = false;
    this.digitValidStatus = false;
    this.digitsStatus = {
      digit1: false,
      digit2: false,
      digit3: false,
      digit4: false,
      digit5: false,
      digit6: false
    };
    this.verificationCodeForm.reset();
    this.emailVerificationService.resendOtp()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        error: () => this.systemUnavailableError = true
      });
  }

  getKeyBoardEvent(event: KeyboardEvent): any {
    return {
      isLeftArrow: this.isIEBrowser ? event.key === Keyboard.LEFT : event.code === Keyboard.LEFT_ARROW,
      isRightArrow: this.isIEBrowser ? event.key === Keyboard.RIGHT : event.code === Keyboard.RIGHT_ARROW,
      isUpArrow: this.isIEBrowser ? event.key === Keyboard.UP : event.code === Keyboard.UP_ARROW,
      isDownArrow: this.isIEBrowser ? event.key === Keyboard.DOWN : event.code === Keyboard.DOWN_ARROW,
      isBackSpace: this.isIEBrowser ? event.key === Keyboard.BACKSPACE : event.code === Keyboard.BACKSPACE,
      isTab: this.isIEBrowser ? event.key === Keyboard.TAB : event.code === Keyboard.TAB,
      isShift: this.isIEBrowser ? event.key === Keyboard.SHIFT : event.code === Keyboard.SHIFT,
      isDelete: this.isIEBrowser ? event.key === Keyboard.DEL : event.code === Keyboard.DELETE
    };
  }

  isEmptyValue(event: KeyboardEvent, regex: RegExp): boolean {
    const keyBoard = this.getKeyBoardEvent(event);
    return (event.target as HTMLInputElement).value.length >= (event.target as HTMLInputElement).maxLength && regex.test(event.key)
      || keyBoard.isBackSpace || keyBoard.isDelete;
  }

  focusNextInputBox(event: KeyboardEvent, index: number): void {
    const keyBoard = this.getKeyBoardEvent(event);
    const inputElements = this.el.nativeElement.querySelectorAll('input');
    if ((index < inputElements.length - 1 && !this.cursorClicked) || (!this.cursorClicked && keyBoard.isRightArrow)) {
      inputElements[Number(index) + 1].focus();
      inputElements[Number(index) + 1].select();
      inputElements.forEach((val, i) => {
        if (i < index && !val.value) {
          this.digitsStatus[`digit${Number(i) + 1}`] = true;
        }
      });
    } else if (index === inputElements.length - 1 && !keyBoard.isRightArrow) {
      this.submitBtn.nativeElement.focus();
    } else {
      this.onKeyPress(event, index);
    }
  }

  onKeyPress(event: KeyboardEvent, index: number): void {
    if ((event.target as HTMLInputElement)?.value?.length > (event.target as HTMLInputElement)?.maxLength) {
      const tempValue = (event.target as HTMLInputElement)?.value.slice(-1);
      (event.target as HTMLInputElement).value = tempValue;
      this.focusNextInputBox(event, index);
    }
  }

  onValueChange(event: KeyboardEvent, index: number): void {
    this.expiredAndIncorrectError = false;
    this.systemUnavailableError = false;
    const keyBoard = this.getKeyBoardEvent(event);
    const regex = new RegExp(/^\d+$/);
    if (regex.test(event.key) || keyBoard.isRightArrow || keyBoard.isLeftArrow || keyBoard.isBackSpace || event.key === 'Unidentified') {
      const inputElements = this.el.nativeElement.querySelectorAll('input');
      /**** -----  To Focus on Next immediate input box  ---- **/
      if (((event.target as HTMLInputElement).value.length === 1 || keyBoard.isRightArrow) && !keyBoard.isLeftArrow) {
        this.focusNextInputBox(event, index);
        /****--- To Focus on Previous input box  ---- **/
      } else if ((!(event.target as HTMLInputElement).value && index && keyBoard.isBackSpace && !this.cursorClicked) ||
        (keyBoard.isLeftArrow && index >= 0)) {
        inputElements[Number(index) - 1].focus();
        inputElements[Number(index) - 1].select();
      } else {
        this.onKeyPress(event, index);
      }
    }
  }

  onClickEvent(index: number): void {
    const inputElements = this.el.nativeElement.querySelectorAll('input');
    if (inputElements[Number(index)].value) {
      this.cursorClicked = true;
    }
  }

  onBlurEvent(index: number): void {
    const validStatus = [];
    const inputElements = this.el.nativeElement.querySelectorAll('input');
    inputElements.forEach((val, i) => {
      if (i < index && !val.value) {
        this.digitsStatus[`digit${Number(i) + 1}`] = true;
        validStatus.push(true);
      } else {
        this.digitsStatus[`digit${Number(i) + 1}`] = false;
      }
    });
    this.digitValidStatus = !!validStatus.length;
    this.expiredAndIncorrectError = false;
    this.cursorClicked = false;
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const keyBoard = this.getKeyBoardEvent(event);
    if (keyBoard.isBackSpace && (event.target as HTMLInputElement).nodeName === 'INPUT') {
      event.preventDefault();
    }
    const regex = new RegExp(/^\d+$/);
    if (this.isEmptyValue(event, regex)) {
      this.verificationCodeForm.controls[`digit${Number(index) + 1}`].setValue('');
    } else if (((keyBoard.isUpArrow || keyBoard.isDownArrow || !regex.test(event.key) ||
        (event.target as HTMLInputElement).value.length >= (event.target as HTMLInputElement).maxLength) &&
      (!keyBoard.isTab && !keyBoard.isShift) && !(event.ctrlKey &&
        (event.code === 'KeyV' || event.key === 'V' || event.key === 'v')))) {
      event.preventDefault();
    }
  }

  pasteValues(element: ClipboardEvent): void {
    const pasteValue = element.clipboardData || window['clipboardData'];
    const trimmedValue = pasteValue.getData('Text');
    const removeSpace = trimmedValue.replace(/\s+/g, '');
    let onlyNumber = removeSpace.match(/\d+/g);
    if (onlyNumber === null) {
      onlyNumber = [];
    } else if (Array.isArray(onlyNumber)) {
      onlyNumber = onlyNumber.join('');
      onlyNumber = onlyNumber.split('');
    }
    this.pasteDigitsSequence(onlyNumber);
    element.preventDefault();
  }

  pasteDigitsSequence(otpCode: string[]): void {
    if (otpCode.length < 6) {
      for (let i = otpCode.length; i < 6; i++) {
        otpCode.push('a');
      }
    }
    for (let i = 0; i < otpCode.length; i++) {
      if (otpCode[i] === 'a') {
        this.verificationCodeForm.controls[`digit${Number(i) + 1}`].setValue('');
      } else {
        this.verificationCodeForm.controls[`digit${Number(i) + 1}`].setValue(otpCode[i]);
        if (i === otpCode.length - 1) {
          this.submitBtn.nativeElement.focus();
        }
      }
    }
  }

  handleChange(): void {
    const firstValue: any = this.verificationCodeForm.controls[`digit1`].value;
    const array = firstValue.toString().split('');
    if (firstValue) {
      if (array.length > 5) {
        this.pasteDigitsSequence(array);
      }
    }
  }

  validatePin(): void {
    this.formSubmit = true;
    this.toggleElement = true;
    this.expiredAndIncorrectError = false;
    this.systemUnavailableError = false;

    if (!this.verificationCodeForm.valid) {
      /***---- If form not valid, mark all inputs as touched  ----**/
      Object.keys(this.verificationCodeForm.controls).forEach((key) => {
        this.verificationCodeForm.controls[key].markAllAsTouched();
        this.toggleElement = true;
      });
      return;
    }

    const allDigitValue = [];
    const inputElements = this.el.nativeElement.querySelectorAll('input');
    inputElements.forEach((input) => {
      allDigitValue.push(input.value);
    });
    const pinInput = allDigitValue.join('');

    this.emailVerificationService.verifyOtp(pinInput)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.verifiedResult = true;
          this.closeModal();
        },
        error: (error: HttpErrorResponse) => this.handleVerifyError(error)
      });
  }

  private handleVerifyError(error: HttpErrorResponse): void {
    this.verificationCodeForm.reset();
    this.formSubmit = false;
    this.toggleElement = false;
    if (error?.status === HttpStatusCode.BadRequest) {
      this.expiredAndIncorrectError = true;
    } else {
      this.systemUnavailableError = true;
    }
  }

  onCancel(): void {
    this.verifiedResult = false;
    this.closeModal();
  }

  private closeModal(): void {
    // Close Popr2's <dialog> before notifying the parent so any view change/navigation runs after the dialog has finished closing.
    this.modalRef?.close();
    this.closed.emit();
  }

  /**
   * Handles the "Change email address" button click.
   * Closes the verification popup and keeps the edit form open so the user can modify their email.
   */
  changeEmail(): void {
    this.changeEmailRequested = true;
    this.onCancel();
  }
}